import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, CircularProgress, InputAdornment, Link, MenuItem, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { Autorenew, CheckCircle, LocalShipping, MoveToInbox, PhotoCamera, Usb, UsbOff, WarningAmber } from '@mui/icons-material';
import { AnimatePresence, motion } from 'framer-motion';
import api from '../../../services/api';
import type { DifferenceTreatment, FactoryMaterial, GrindingTrip, WeighSource } from '../../../types/factory';
import { TONES, Tone } from '../../../components/ui/PageKit';
import { compressPhoto, errorMessage, fmtMoney, fmtQty, num } from '../shared';
import { PlantData, fmtKg, fmtWhen } from './data';
import { useSerialScale } from './serialScale';
import {
  ACCENT, ACCENT_SOFT, AnimatedNumber, INK, LINE, MUTED, PanelShell, PanelTitle, PulseDot, SURFACE, Stagger, StatBox,
  accentButton, outlineButton,
} from './ui';

type Mode = 'in' | 'out' | 'return';

const MODES: { value: Mode; label: string; icon: React.ReactNode }[] = [
  { value: 'in', label: 'Ingreso de compra', icon: <MoveToInbox fontSize="small" /> },
  { value: 'out', label: 'Salida a molienda', icon: <LocalShipping fontSize="small" /> },
  { value: 'return', label: 'Retorno de molienda', icon: <Autorenew fontSize="small" /> },
];

const TREATMENTS: { value: DifferenceTreatment; label: string }[] = [
  { value: 'Reclamo', label: 'Recibir peso real y dejar reclamo' },
  { value: 'NotaCredito', label: 'Recibir peso real y pedir nota de crédito' },
  { value: 'Aceptada', label: 'Aceptar la diferencia' },
];

/** Precio de molienda afuera según las planillas (oct 2026). */
const DEFAULT_GRIND_PRICE = 350;

type Supplier = { id: number; name: string };

const diffTone = (pct: number): Tone => (Math.abs(pct) < 0.5 ? 'ok' : Math.abs(pct) <= 2 ? 'warn' : 'bad');
/** Merma de un viaje a molienda: hasta 3% es normal, más de 6% hay que mirarlo. */
const shrinkTone = (pct: number): Tone => (pct >= -3 ? 'ok' : pct >= -6 ? 'warn' : 'bad');
const signed = (n: number, unit = 'kg') => `${n > 0 ? '+' : n < 0 ? '−' : ''}${fmtQty(Math.abs(n))} ${unit}`;
const signedPct = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n).toFixed(2).replace('.', ',')} %`;

const ScalePanel: React.FC<{ data: PlantData; onSaved: () => void; onClose: () => void }> = ({ data, onSaved, onClose }) => {
  const scale = useSerialScale();
  const [mode, setMode] = useState<Mode>('in');
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);

  // Pesaje: bruto capturado de la balanza (o de la foto, o tipeado) y tara.
  const [captured, setCaptured] = useState<{ gross: number; at: Date; source: WeighSource; photoId?: number } | null>(null);
  const [grossInput, setGrossInput] = useState('');
  const [tareInput, setTareInput] = useState('');
  const [reading, setReadingPhoto] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getSuppliers({ page: 1, pageSize: 500 })
      .then(r => setSuppliers((r.suppliers || []).map((s: any) => ({ id: s.id, name: s.name }))))
      .catch(() => setSuppliers([]));
  }, []);

  const live = scale.state === 'connected' ? scale.reading : null;
  const gross = captured?.gross ?? (grossInput !== '' ? num(grossInput) : live?.kg ?? null);
  const tare = num(tareInput);
  const net = gross != null ? Math.round((gross - tare) * 100) / 100 : null;
  const source: WeighSource = captured?.source ?? (live && grossInput === '' ? 'balanza' : 'manual');

  const resetWeighing = () => { setCaptured(null); setGrossInput(''); setTareInput(''); };
  const switchMode = (m: Mode) => { setMode(m); setError(null); setDone(null); resetWeighing(); };

  const capture = () => {
    if (!live) return;
    setCaptured({ gross: live.kg, at: new Date(), source: 'balanza' });
    setGrossInput('');
  };

  const readPhoto = async (file?: File | null) => {
    if (!file) return;
    setReadingPhoto(true);
    setError(null);
    try {
      const r = await api.readScale(await compressPhoto(file));
      if (r.kg == null) setError(r.note || 'No se pudo leer el visor: cargá el peso a mano');
      else setCaptured({ gross: r.kg, at: new Date(r.takenAt), source: 'foto', photoId: r.photoId });
    } catch (e) {
      setError(errorMessage(e, 'No se pudo leer la foto'));
    } finally {
      setReadingPhoto(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const weighOk = net != null && net > 0;
  const weighing = { grossKg: gross, tareKg: tare || (gross != null ? 0 : null), kg: net ?? 0, weighSource: source, photoId: captured?.photoId };

  const finish = (msg: string) => {
    setDone(msg);
    resetWeighing();
    onSaved();
  };

  return (
    <PanelShell
      title={<>Balanza <Box component="span" sx={{ color: MUTED, fontWeight: 700 }}>· Ingreso y egreso</Box></>}
      onClose={onClose}
      badge={<ConnectionBadge state={scale.state} onConnect={() => scale.connect()} onDisconnect={scale.disconnect} />}
    >
      <Stack direction="row" spacing={1} sx={{ mt: 1.5, overflowX: 'auto', pb: 0.5 }}>
        {MODES.map(m => {
          const on = m.value === mode;
          return (
            <Button
              key={m.value}
              onClick={() => switchMode(m.value)}
              startIcon={m.icon}
              sx={{
                flex: 1, minWidth: 150, py: 1.1, borderRadius: 2, textTransform: 'none', fontWeight: 650, fontSize: 14, whiteSpace: 'nowrap',
                bgcolor: on ? ACCENT : '#fff', color: on ? '#fff' : INK, border: `1px solid ${on ? ACCENT : LINE}`,
                '&:hover': { bgcolor: on ? ACCENT : ACCENT_SOFT },
              }}
            >
              {m.label}
            </Button>
          );
        })}
      </Stack>

      {/* Lectura de la balanza */}
      <Box sx={{ mt: 2, bgcolor: SURFACE, borderRadius: 3, p: 2.25 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, color: MUTED, fontSize: 14 }}>Peso neto</Typography>
            <Typography sx={{ fontSize: { xs: 48, md: 60 }, fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1.05, fontVariantNumeric: 'tabular-nums', color: INK }}>
              {net == null ? <Box component="span" sx={{ color: '#C4C2BD' }}>— </Box> : <AnimatedNumber value={net} duration={live && !captured ? 0.25 : 0.8} />}
              <Box component="span" sx={{ fontSize: '0.45em', ml: 1, fontWeight: 700 }}>kg</Box>
            </Typography>
            <ReadingState live={!!live && !captured} stable={!!live?.stable} captured={captured} connected={scale.state === 'connected'} />
          </Box>
          <Stack spacing={1} sx={{ width: { xs: '100%', sm: 190 }, flexShrink: 0 }}>
            {captured ? (
              <Box sx={{ alignSelf: { sm: 'flex-end' }, bgcolor: '#E3E9FB', color: '#2B3BB8', px: 1.25, py: 0.4, borderRadius: 999, fontSize: 12.5, fontWeight: 650 }}>
                Pesaje capturado · {captured.at.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
              </Box>
            ) : null}
            <TextField
              size="small" label="Bruto" value={captured ? fmtQty(captured.gross) : grossInput}
              placeholder={live ? fmtQty(live.kg) : ''}
              onChange={e => { setCaptured(null); setGrossInput(e.target.value); }}
              InputProps={{ endAdornment: <InputAdornment position="end">kg</InputAdornment> }}
              inputProps={{ inputMode: 'decimal' }}
            />
            <TextField
              size="small" label="Tara" value={tareInput} onChange={e => setTareInput(e.target.value)}
              InputProps={{ endAdornment: <InputAdornment position="end">kg</InputAdornment> }}
              inputProps={{ inputMode: 'decimal' }}
            />
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
          {scale.state === 'connected' && (
            <Button size="small" variant="contained" disabled={!live?.stable} onClick={capture} sx={{ ...accentButton, py: 0.6, fontSize: 13.5 }}>
              {live?.stable ? 'Capturar peso' : 'Esperando lectura estable…'}
            </Button>
          )}
          <Button size="small" variant="outlined" startIcon={reading ? <CircularProgress size={14} /> : <PhotoCamera />} disabled={reading}
            onClick={() => fileRef.current?.click()} sx={{ ...outlineButton, py: 0.6, fontSize: 13.5 }}>
            {reading ? 'Leyendo el visor…' : 'Foto del visor'}
          </Button>
          {(captured || grossInput || tareInput) && (
            <Button size="small" onClick={resetWeighing} sx={{ textTransform: 'none', color: MUTED }}>Pesar de nuevo</Button>
          )}
          <input ref={fileRef} type="file" accept="image/*" capture="environment" hidden onChange={e => readPhoto(e.target.files?.[0])} />
        </Stack>
        {scale.error && <Typography variant="caption" sx={{ color: TONES.bad.fg, display: 'block', mt: 1 }}>{scale.error}</Typography>}
      </Box>

      {error && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <AnimatePresence>
        {done && (
          <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mt: 2, bgcolor: TONES.ok.bg, color: TONES.ok.fg, borderRadius: 2, px: 2, py: 1.25 }}>
              <motion.div initial={{ scale: 0, rotate: -30 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: 'spring', stiffness: 400, damping: 15 }} style={{ display: 'flex' }}>
                <CheckCircle />
              </motion.div>
              <Typography sx={{ fontWeight: 650, flex: 1 }}>{done}</Typography>
              <Button size="small" onClick={() => setDone(null)} sx={{ color: TONES.ok.fg, textTransform: 'none' }}>Cerrar</Button>
            </Stack>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        <motion.div key={mode} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.2 }}>
          {mode === 'in' && (
            <PurchaseForm
              data={data} suppliers={suppliers} net={net} weighOk={weighOk} saving={saving}
              onConfirm={async payload => {
                setSaving(true); setError(null);
                try {
                  const r = await api.createReception({ ...payload, ...weighing });
                  finish(`Ingreso ${r.lotCode} · +${fmtQty(r.kg)} kg de ${r.materialName}${r.remitoNumber ? '' : ' · pendiente de vincular comprobante'}`);
                  return true;
                } catch (e) { setError(errorMessage(e, 'No se pudo registrar el ingreso')); return false; } finally { setSaving(false); }
              }}
            />
          )}
          {mode === 'out' && (
            <GrindingOutForm
              data={data} suppliers={suppliers} net={net} weighOk={weighOk} saving={saving}
              onConfirm={async payload => {
                setSaving(true); setError(null);
                try {
                  const r = await api.sendToGrinding({ ...payload, grossKg: weighing.grossKg, tareKg: weighing.tareKg, kg: weighing.kg });
                  finish(`Salida ${r.code} · ${fmtQty(r.sentKg)} kg a molienda`);
                  return true;
                } catch (e) { setError(errorMessage(e, 'No se pudo registrar la salida')); return false; } finally { setSaving(false); }
              }}
            />
          )}
          {mode === 'return' && (
            <GrindingReturnForm
              data={data} net={net} weighOk={weighOk} saving={saving}
              onConfirm={async (tripId, payload) => {
                setSaving(true); setError(null);
                try {
                  const r = await api.returnFromGrinding(tripId, { ...payload, grossKg: weighing.grossKg, tareKg: weighing.tareKg, kg: weighing.kg });
                  finish(`Retorno ${r.code} · +${fmtQty(r.returnedKg)} kg en lote ${r.returnLotCode}`);
                  return true;
                } catch (e) { setError(errorMessage(e, 'No se pudo registrar el retorno')); return false; } finally { setSaving(false); }
              }}
            />
          )}
        </motion.div>
      </AnimatePresence>

      <RecentWeighings data={data} />
    </PanelShell>
  );
};

// ==================== Estado de la balanza ====================

const ConnectionBadge: React.FC<{ state: string; onConnect: () => void; onDisconnect: () => void }> = ({ state, onConnect, onDisconnect }) => {
  if (state === 'connected') {
    return (
      <Button size="small" onClick={onDisconnect} startIcon={<PulseDot tone="ok" size={8} pulse />}
        sx={{ bgcolor: TONES.ok.bg, color: TONES.ok.fg, borderRadius: 999, textTransform: 'none', fontWeight: 650, px: 1.5, '&:hover': { bgcolor: '#D7EEE2' } }}>
        Balanza conectada
      </Button>
    );
  }
  if (state === 'unsupported') {
    return (
      <Box title="La conexión por cable funciona en Chrome o Edge de computadora" sx={{ bgcolor: '#F0F0EE', color: MUTED, borderRadius: 999, px: 1.5, py: 0.5, fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
        <UsbOff sx={{ fontSize: 16 }} /> Sin cable: foto o a mano
      </Box>
    );
  }
  return (
    <Button size="small" onClick={onConnect} disabled={state === 'connecting'} startIcon={state === 'connecting' ? <CircularProgress size={14} /> : <Usb />}
      sx={{ bgcolor: '#fff', border: `1px solid ${LINE}`, color: INK, borderRadius: 999, textTransform: 'none', fontWeight: 650, px: 1.5 }}>
      {state === 'connecting' ? 'Conectando…' : 'Conectar balanza'}
    </Button>
  );
};

const ReadingState: React.FC<{ live: boolean; stable: boolean; connected: boolean; captured: { source: WeighSource } | null }> = ({ live, stable, connected, captured }) => {
  let tone: Tone = 'neutral';
  let text = 'Cargá el peso, sacale foto al visor o conectá la balanza';
  if (captured) { tone = 'ok'; text = captured.source === 'foto' ? 'Leído de la foto del visor' : 'Lectura estable capturada'; }
  else if (live) { tone = stable ? 'ok' : 'warn'; text = stable ? 'Lectura estable' : 'Moviéndose: esperá que se quede quieta'; }
  else if (connected) { tone = 'warn'; text = 'Esperando datos de la balanza'; }
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
      <PulseDot tone={tone} size={10} pulse={live && !stable} />
      <Typography sx={{ fontWeight: 650, color: tone === 'neutral' ? MUTED : TONES[tone].fg, fontSize: 14 }}>{text}</Typography>
    </Stack>
  );
};

// ==================== Ingreso de compra ====================

const materialLabel = (m: FactoryMaterial) => `${m.name}${m.code ? ` · ${m.code}` : ''}`;
const ORIGIN_SECTOR: Record<string, string> = { Virgen: 'Materia prima virgen', RecuperadaPropia: 'Recuperado', RecuperadaComprada: 'Recuperado' };

const PurchaseForm: React.FC<{
  data: PlantData; suppliers: Supplier[]; net: number | null; weighOk: boolean; saving: boolean;
  onConfirm: (p: { materialId: number; supplierId?: number | null; remitoNumber?: string; documentKg?: number | null; unitCost?: number | null; differenceTreatment?: DifferenceTreatment | null; notes?: string }) => Promise<boolean>;
}> = ({ data, suppliers, net, weighOk, saving, onConfirm }) => {
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [material, setMaterial] = useState<FactoryMaterial | null>(null);
  const [hasDoc, setHasDoc] = useState(true);
  const [docNumber, setDocNumber] = useState('');
  const [docKg, setDocKg] = useState('');
  const [price, setPrice] = useState('');
  const [treatment, setTreatment] = useState<DifferenceTreatment>('Reclamo');

  const documentKg = hasDoc && docKg !== '' ? num(docKg) : null;
  const diff = documentKg && net != null ? net - documentKg : null;
  const diffPct = diff != null && documentKg ? (diff / documentKg) * 100 : null;
  const current = material ? (data.materials.find(m => m.id === material.id)?.currentStock ?? material.currentStock) : null;

  const submit = async () => {
    if (!material) return;
    const ok = await onConfirm({
      materialId: material.id, supplierId: supplier?.id ?? null,
      remitoNumber: hasDoc ? docNumber.trim() || undefined : undefined,
      documentKg, unitCost: price !== '' ? num(price) : null,
      differenceTreatment: diff ? treatment : null,
    });
    if (ok) { setDocNumber(''); setDocKg(''); setPrice(''); }
  };

  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mt: 2.5 }}>
        <Autocomplete size="small" options={suppliers} value={supplier} onChange={(_, v) => setSupplier(v)}
          getOptionLabel={o => o.name} isOptionEqualToValue={(a, b) => a.id === b.id}
          renderInput={p => <TextField {...p} label="Proveedor" />} />
        <TextField size="small" label="Lote" value="Se numera al confirmar (R-…)" disabled />
        <Autocomplete size="small" options={data.materials} value={material} onChange={(_, v) => setMaterial(v)}
          getOptionLabel={materialLabel} isOptionEqualToValue={(a, b) => a.id === b.id}
          renderInput={p => <TextField {...p} label="Material" required />} />
        <TextField size="small" label="Destino" value={material ? (ORIGIN_SECTOR[material.origin || ''] || 'Depósito de MP') : ''} disabled />
      </Box>

      <PanelTitle action={
        <Stack direction="row" alignItems="center">
          <Typography variant="body2" sx={{ color: MUTED }}>Tengo el comprobante</Typography>
          <Switch size="small" checked={hasDoc} onChange={e => setHasDoc(e.target.checked)} />
        </Stack>
      }>Contraste con comprobante</PanelTitle>

      {hasDoc ? (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1.4fr 1fr 1fr' }, gap: 1.5 }}>
            <TextField size="small" label="Factura o remito" placeholder="A · 0001-00001234" value={docNumber} onChange={e => setDocNumber(e.target.value)} />
            <TextField size="small" label="Kg del comprobante" value={docKg} onChange={e => setDocKg(e.target.value)} inputProps={{ inputMode: 'decimal' }}
              InputProps={{ endAdornment: <InputAdornment position="end">kg</InputAdornment> }} />
            <TextField size="small" label="Precio por kg" value={price} onChange={e => setPrice(e.target.value)} inputProps={{ inputMode: 'decimal' }}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
          </Box>
          {documentKg != null && (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1, mt: 1.5 }}>
              <StatBox label="Según comprobante" value={fmtKg(documentKg)} />
              <StatBox label="Neto pesado" value={fmtKg(net)} />
              <StatBox label="Diferencia" tone={diffPct != null ? diffTone(diffPct) : undefined}
                value={diff != null ? signed(diff) : '—'} hint={diffPct != null ? signedPct(diffPct) : undefined} />
            </Box>
          )}
          {diff != null && Math.abs(diff) > 0.009 && (
            <Box sx={{ mt: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ color: TONES.warn.fg, mb: 1 }}>
                <WarningAmber fontSize="small" />
                <Typography variant="body2" sx={{ fontWeight: 650 }}>
                  {diff < 0 ? 'Llegó menos de lo facturado' : 'Llegó más de lo facturado'}: al stock entra lo que pesó la balanza.
                </Typography>
              </Stack>
              <TextField select size="small" fullWidth label="Tratamiento" value={treatment} onChange={e => setTreatment(e.target.value as DifferenceTreatment)}>
                {TREATMENTS.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </TextField>
            </Box>
          )}
        </>
      ) : (
        <Box sx={{ bgcolor: ACCENT_SOFT, borderRadius: 2, px: 2, py: 1.5 }}>
          <Typography variant="body2">
            Queda como <b>recepción pendiente de vincular</b>: el pesaje es el respaldo para la factura del proveedor. Cuando llegue, se vincula desde la Oficina.
          </Typography>
        </Box>
      )}

      <PanelTitle>Impacto al confirmar</PanelTitle>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ flex: 1 }}><StatBox label="Stock actual" value={current != null ? <AnimatedNumber value={current} suffix=" kg" /> : '—'} /></Box>
        <Typography sx={{ fontSize: 22, color: MUTED }}>+</Typography>
        <Box sx={{ flex: 1 }}><StatBox label="Ingreso" value={net != null ? `${fmtQty(net)} kg` : '—'} /></Box>
        <Typography sx={{ fontSize: 22, color: MUTED }}>=</Typography>
        <Box sx={{ flex: 1 }}><StatBox label="Nuevo stock" tone="ok" value={current != null && net != null ? <AnimatedNumber value={current + net} suffix=" kg" /> : '—'} /></Box>
      </Stack>
      <Typography variant="caption" sx={{ color: MUTED, display: 'block', mt: 1 }}>El stock se actualiza una sola vez, al confirmar. El lote queda para trazar en qué mezclas se usó.</Typography>

      <Stack spacing={1} sx={{ mt: 2 }}>
        <Button variant="contained" size="large" disabled={!weighOk || !material || saving} onClick={submit} startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <MoveToInbox />} sx={accentButton}>
          {weighOk ? `Confirmar ingreso · +${fmtQty(net)} kg` : 'Confirmar ingreso'}
        </Button>
        {hasDoc && (
          <Link component="button" type="button" underline="always" onClick={() => setHasDoc(false)} sx={{ color: '#2B3BB8', alignSelf: 'center', fontSize: 14 }}>
            Sin factura: crear recepción pendiente de vincular
          </Link>
        )}
      </Stack>
    </Box>
  );
};

// ==================== Salida a molienda ====================

const GrindingOutForm: React.FC<{
  data: PlantData; suppliers: Supplier[]; net: number | null; weighOk: boolean; saving: boolean;
  onConfirm: (p: { materialId?: number | null; description?: string; supplierId?: number | null; pricePerKg?: number | null; notes?: string }) => Promise<boolean>;
}> = ({ data, suppliers, net, weighOk, saving, onConfirm }) => {
  const [fromStock, setFromStock] = useState(false);
  const [material, setMaterial] = useState<FactoryMaterial | null>(null);
  const [description, setDescription] = useState('Rotos de producción');
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [price, setPrice] = useState(String(DEFAULT_GRIND_PRICE));
  const open = data.grinding.filter(g => g.status === 'Enviado');
  const current = material ? material.currentStock : null;
  const cost = net != null && supplier ? net * num(price) : null;

  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mt: 2.5 }}>
        <TextField select size="small" label="Qué sale" value={fromStock ? 'stock' : 'rotos'} onChange={e => setFromStock(e.target.value === 'stock')}>
          <MenuItem value="rotos">Rotos o scrap (no se llevan en stock)</MenuItem>
          <MenuItem value="stock">Materia prima del stock</MenuItem>
        </TextField>
        {fromStock ? (
          <Autocomplete size="small" options={data.materials} value={material} onChange={(_, v) => setMaterial(v)}
            getOptionLabel={materialLabel} isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={p => <TextField {...p} label="Material" required />} />
        ) : (
          <TextField size="small" label="Descripción" value={description} onChange={e => setDescription(e.target.value)} />
        )}
        <Autocomplete size="small" options={suppliers} value={supplier} onChange={(_, v) => setSupplier(v)}
          getOptionLabel={o => o.name} isOptionEqualToValue={(a, b) => a.id === b.id}
          renderInput={p => <TextField {...p} label="Molino" placeholder="Vacío = se muele adentro" />} />
        <TextField size="small" label="Precio de molienda por kg" value={price} onChange={e => setPrice(e.target.value)} disabled={!supplier}
          inputProps={{ inputMode: 'decimal' }} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
      </Box>

      <PanelTitle>Impacto al confirmar</PanelTitle>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1 }}>
        <StatBox label="Sale a molienda" value={net != null ? `${fmtQty(net)} kg` : '—'} />
        <StatBox label={fromStock ? 'Queda en stock' : 'Stock'} value={fromStock ? (current != null && net != null ? <AnimatedNumber value={current - net} suffix=" kg" /> : '—') : 'No cambia'}
          tone={fromStock && current != null && net != null && current - net < 0 ? 'bad' : undefined} />
        <StatBox label="Costo de molienda" value={cost != null ? fmtMoney(cost) : supplier ? '—' : 'Propia'} />
      </Box>
      <Typography variant="caption" sx={{ color: MUTED, display: 'block', mt: 1 }}>
        Cuando vuelva, se pesa en “Retorno de molienda” y queda la merma del viaje.
      </Typography>

      <Button fullWidth variant="contained" size="large" sx={{ ...accentButton, mt: 2 }}
        disabled={!weighOk || saving || (fromStock ? !material : !description.trim())}
        startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <LocalShipping />}
        onClick={() => onConfirm({
          materialId: fromStock ? material?.id : null, description: fromStock ? undefined : description.trim(),
          supplierId: supplier?.id ?? null, pricePerKg: supplier && price !== '' ? num(price) : null,
        })}>
        {weighOk ? `Registrar salida · −${fmtQty(net)} kg` : 'Registrar salida'}
      </Button>

      <PanelTitle>En molienda ahora</PanelTitle>
      {open.length ? (
        <Stagger>{open.map(g => <TripRow key={g.id} trip={g} />)}</Stagger>
      ) : (
        <Typography variant="body2" sx={{ color: MUTED }}>No hay material afuera.</Typography>
      )}
    </Box>
  );
};

const TripRow: React.FC<{ trip: GrindingTrip; selected?: boolean; onClick?: () => void }> = ({ trip, selected, onClick }) => {
  const days = Math.floor((Date.now() - new Date(trip.sentAt).getTime()) / 86_400_000);
  return (
    <Box onClick={onClick} sx={{
      border: `1.5px solid ${selected ? ACCENT : LINE}`, bgcolor: selected ? ACCENT_SOFT : '#fff', borderRadius: 2, px: 1.75, py: 1.25,
      cursor: onClick ? 'pointer' : 'default', transition: 'all .15s',
    }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography sx={{ fontWeight: 700, flex: 1 }}>{trip.code}</Typography>
        <Typography sx={{ fontWeight: 750, fontVariantNumeric: 'tabular-nums' }}>{fmtKg(trip.sentKg)}</Typography>
      </Stack>
      <Typography variant="body2" sx={{ color: MUTED }}>
        {trip.materialName || trip.description} · {trip.supplierName || 'Molienda propia'} · {fmtWhen(trip.sentAt)}
        {days >= 1 ? ` · hace ${days} día${days > 1 ? 's' : ''}` : ''}
      </Typography>
    </Box>
  );
};

// ==================== Retorno de molienda ====================

const GrindingReturnForm: React.FC<{
  data: PlantData; net: number | null; weighOk: boolean; saving: boolean;
  onConfirm: (tripId: number, p: { materialId: number; notes?: string }) => Promise<boolean>;
}> = ({ data, net, weighOk, saving, onConfirm }) => {
  const open = useMemo(() => data.grinding.filter(g => g.status === 'Enviado'), [data.grinding]);
  const [tripId, setTripId] = useState<number | null>(open[0]?.id ?? null);
  const recovered = useMemo(
    () => [...data.materials].sort((a, b) => Number(b.origin === 'RecuperadaPropia') - Number(a.origin === 'RecuperadaPropia')),
    [data.materials],
  );
  const [material, setMaterial] = useState<FactoryMaterial | null>(recovered.find(m => m.origin === 'RecuperadaPropia') ?? null);
  const trip = open.find(g => g.id === tripId) ?? null;

  useEffect(() => { if (!trip && open.length) setTripId(open[0].id); }, [open, trip]);

  if (!open.length) {
    return (
      <Box sx={{ mt: 3, textAlign: 'center', py: 4, bgcolor: SURFACE, borderRadius: 3 }}>
        <Typography sx={{ fontWeight: 700 }}>No hay nada en molienda</Typography>
        <Typography variant="body2" sx={{ color: MUTED, mt: 0.5 }}>Primero registrá la salida: el retorno se vincula con ese viaje.</Typography>
      </Box>
    );
  }

  const diff = trip && net != null ? net - trip.sentKg : null;
  const diffPct = diff != null && trip ? (diff / trip.sentKg) * 100 : null;
  const costPerKg = trip?.pricePerKg && net ? (trip.pricePerKg * trip.sentKg) / net : null;
  const current = material?.currentStock ?? null;

  return (
    <Box>
      <PanelTitle>¿Qué viaje vuelve?</PanelTitle>
      <Stack spacing={1}>
        {open.map(g => <TripRow key={g.id} trip={g} selected={g.id === tripId} onClick={() => setTripId(g.id)} />)}
      </Stack>

      <Autocomplete size="small" sx={{ mt: 2 }} options={recovered} value={material} onChange={(_, v) => setMaterial(v)}
        getOptionLabel={materialLabel} isOptionEqualToValue={(a, b) => a.id === b.id}
        groupBy={o => (o.origin === 'RecuperadaPropia' ? 'Recuperada propia' : 'Otras')}
        renderInput={p => <TextField {...p} label="Entra como" required helperText="La MP recuperada en la que queda lo molido" />} />

      <PanelTitle>Salió y volvió</PanelTitle>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1 }}>
        <StatBox label="Salió" value={fmtKg(trip?.sentKg)} hint={trip ? fmtWhen(trip.sentAt) : undefined} />
        <StatBox label="Volvió" value={fmtKg(net)} />
        <StatBox label={diff != null && diff > 0 ? 'Diferencia' : 'Merma'} tone={diffPct != null ? shrinkTone(diffPct) : undefined}
          value={diff != null ? signed(diff) : '—'} hint={diffPct != null ? signedPct(diffPct) : undefined} />
      </Box>
      {costPerKg != null && (
        <Typography variant="body2" sx={{ color: MUTED, mt: 1 }}>
          Molienda: {fmtMoney(trip!.cost)} → <b>{fmtMoney(costPerKg)} por kg</b> de lo que volvió.
        </Typography>
      )}

      <PanelTitle>Impacto al confirmar</PanelTitle>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ flex: 1 }}><StatBox label="Stock actual" value={current != null ? <AnimatedNumber value={current} suffix=" kg" /> : '—'} /></Box>
        <Typography sx={{ fontSize: 22, color: MUTED }}>+</Typography>
        <Box sx={{ flex: 1 }}><StatBox label="Retorno" value={net != null ? `${fmtQty(net)} kg` : '—'} /></Box>
        <Typography sx={{ fontSize: 22, color: MUTED }}>=</Typography>
        <Box sx={{ flex: 1 }}><StatBox label="Nuevo stock" tone="ok" value={current != null && net != null ? <AnimatedNumber value={current + net} suffix=" kg" /> : '—'} /></Box>
      </Stack>
      <Typography variant="caption" sx={{ color: MUTED, display: 'block', mt: 1 }}>No cuenta como compra: entra como lote RM-… de recuperada propia, vinculado a {trip?.code}.</Typography>

      <Button fullWidth variant="contained" size="large" sx={{ ...accentButton, mt: 2 }}
        disabled={!weighOk || !trip || !material || saving}
        startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Autorenew />}
        onClick={() => trip && material && onConfirm(trip.id, { materialId: material.id })}>
        {weighOk ? `Confirmar retorno · +${fmtQty(net)} kg` : 'Confirmar retorno'}
      </Button>
    </Box>
  );
};

// ==================== Últimos pesajes ====================

const RecentWeighings: React.FC<{ data: PlantData }> = ({ data }) => {
  const rows = useMemo(() => {
    const items: { key: string; at: string; title: string; detail: string; kg: number; tone: Tone }[] = [];
    data.receptions.slice(0, 8).forEach(r => items.push({
      key: `r${r.id}`, at: r.receivedAt, title: `Ingreso · ${r.materialName}`, kg: r.kg,
      detail: [r.lotCode, r.supplierName, r.remitoNumber ? `Comp. ${r.remitoNumber}` : 'Sin comprobante',
        r.differenceKg ? `dif. ${signed(r.differenceKg)}` : null].filter(Boolean).join(' · '),
      tone: !r.remitoNumber || r.differenceTreatment === 'Reclamo' ? 'warn' : 'ok',
    }));
    data.grinding.slice(0, 8).forEach(g => {
      items.push({ key: `o${g.id}`, at: g.sentAt, title: `Salida a molienda · ${g.materialName || g.description}`, kg: -g.sentKg, detail: [g.code, g.supplierName || 'Propia'].join(' · '), tone: 'info' });
      if (g.returnedAt && g.returnedKg != null) {
        items.push({ key: `v${g.id}`, at: g.returnedAt, title: `Retorno de molienda · ${g.returnMaterialName}`, kg: g.returnedKg,
          detail: [g.code, g.returnLotCode, g.differenceKg != null ? `merma ${signed(g.differenceKg)}` : null].filter(Boolean).join(' · '), tone: 'ok' });
      }
    });
    return items.sort((a, b) => b.at.localeCompare(a.at)).slice(0, 6);
  }, [data.receptions, data.grinding]);

  if (!rows.length) return null;
  return (
    <>
      <PanelTitle>Últimos pesajes</PanelTitle>
      <Stagger gap={0.75}>
        {rows.map(r => (
          <Stack key={r.key} direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.75, borderBottom: `1px solid ${LINE}` }}>
            <PulseDot tone={r.tone} size={8} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 650, fontSize: 14 }} noWrap>{r.title}</Typography>
              <Typography variant="caption" sx={{ color: MUTED }} noWrap component="div">{fmtWhen(r.at)} · {r.detail}</Typography>
            </Box>
            <Typography sx={{ fontWeight: 750, fontVariantNumeric: 'tabular-nums', color: r.kg < 0 ? TONES.bad.fg : TONES.ok.fg, whiteSpace: 'nowrap' }}>
              {signed(r.kg)}
            </Typography>
          </Stack>
        ))}
      </Stagger>
    </>
  );
};

export default ScalePanel;
